/**
 * Presentation helpers for Track Sent Emails.
 *
 * These were previously duplicated inside EmailActivitySubtable.vue, where the
 * row and its expanded detail were the same component. Splitting the detail out
 * into a drawer means both need them, so they live here - and being plain
 * functions rather than component methods, they're directly testable.
 *
 * Date handling deliberately reproduces the previous behaviour exactly
 * (getLocalDate -> global formatDate mixin) so displayed dates don't shift
 * during a refactor that's meant to be invisible to users.
 * ts-enforce-ignore
 */

const dateTimeFormat = new Intl.DateTimeFormat('default', {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit'
});

/**
 * The API returns UTC without a trailing Z, so it has to be marked as UTC
 * before the browser will convert it to local time.
 */
export function getLocalDate(dateString) {
  if (!dateString) return '';
  const utc = dateString.toUpperCase().endsWith('Z') ? dateString : `${dateString}Z`;
  return new Date(utc).toLocaleString();
}

/** Matches the global formatDate mixin in main.js. */
export function formatDate(dateString) {
  if (!dateString) return '';
  return dateTimeFormat.format(new Date(dateString));
}

/** Sent date as shown in the table: UTC -> local, then short date. */
export function formatSentDate(sentDate) {
  if (!sentDate) return '';
  return formatDate(getLocalDate(sentDate));
}

/**
 * One status for the whole email: the shared recipient status when every
 * recipient agrees, otherwise Paid/Mixed.
 */
export function getStatus(email) {
  if (!email?.recipients?.length) return '';
  if (email.recipients.every(x => x.status === email.recipients[0].status)) {
    return email.recipients[0].status;
  }
  if (email.paid) return 'Paid';
  return 'Mixed';
}

/**
 * Display label for the message type. `reminderNumber` is only meaningful for
 * invoice reminders, where the label is "Reminder 1", "Reminder 2", ...
 */
export function getMessageType(email, reminderNumber) {
  switch (email?.messageType) {
    case 'invoiceEmail':
    case 'customEmail':
    case 'combinedInvoiceEmail':
      return 'Invoice';
    case 'trustRequestEmail':
      return 'Trust Request';
    case 'trustRequestReminderEmail':
      return 'Trust Request Reminder';
    case 'invoiceReminderEmail':
      return `Reminder ${reminderNumber || 1}`;
    default:
      return '';
  }
}

/**
 * Reminders are numbered in send order, so a reminder's label depends on its
 * position among the invoice reminders in the same thread - not its index in
 * the reminders array, which also holds other message types.
 */
export function getReminderNumber(reminders, emailLogId, messageType) {
  if (messageType !== 'invoiceReminderEmail') return undefined;
  const invoiceReminders = (reminders || []).filter(x => x.messageType === 'invoiceReminderEmail');
  const position = invoiceReminders.findIndex(x => x.emailLogId === emailLogId);
  return position === -1 ? 1 : position + 1;
}